import api from "@/utils/api";
import router from "@/router";
import { v4 as uuidv4 } from 'uuid';
import { saveData, loadData } from "@/utils/storageHelper";
import { orderActive, orderInactive } from "@/utils/wsHelper";
import { subtotalItem, appError } from "@/utils/helpers";
import logger from "@/utils/logger";
import { datadogRum } from '@datadog/browser-rum';

function createLogger(state, logger) {
  return {
    log(level, message, context = {}) {
      const enrichedContext = { ...context, orderRef: state.orderRef };
      logger.log(level, message, enrichedContext);
    },
    info(message, context = {}) {
      this.log('info', message, context);
    },
    error(message, context = {}) {
      this.log('error', message, context);
    },
  };
}

function loadState() {
  return loadData("orderState");
}

function setState(state) {
  saveData("orderState", state);
}

function calculateTotals(items, tip = 0, taxTotal = 0, discount = 0, surchargesTotal = 0) {
  const subtotal = items.reduce((acc, item) => acc + subtotalItem(item), 0);
  const grandTotal = Math.max(subtotal - discount, 0) + tip + taxTotal + surchargesTotal;
  return { subtotal, grandTotal };
}


const newState = JSON.stringify({
  posTotaled: false,
  orderRef: null,
  started: false,
  isOrderConfirmed: false,
  complete: false,
  status: "NEW",
  confirmedOrderNumber: null,
  submittedOrderNumber: null,
  optIn: false,
  timeoutActive: false,
  askingQuestions: false,
  appRef: null,
  orderTypeId: null,
  type: null,
  ticketName: null,
  items: [],
  name: null,
  phone: null,
  phoneCountryCode: null,
  email: null,
  extraDetails: [],
  paymentType: null,
  paymentDetails: null,
  invoiceNumber: null,
  discounts: 0,
  subtotal: 0,
  tip: 0,
  tableTentNumber: null,
  taxTotal: 0,
  orderStatusInterval: null,
  totalsLoading: false,
  barcodeLookup: null,
  barcodeActive: true,
  coupon: null,
  startTime: null,
  surcharges: [],
  posGrandTotal: 0,
  posSubtotal: 0,
  gpattTicket: null,
  receiptData: null,
  datadogEnabled: false,
  acceptedMessages: [],
});

export default {
  namespaced: true,
  state() {
    const savedState = loadState();
    return savedState || JSON.parse(newState);
  },
  getters: {
    posTotaled: (state) => state.posTotaled,
    logger: (state) => createLogger(state, logger),
    appRef: (state) => state.appRef,
    orderRef: (state) => state.orderRef,
    started: (state) => state.started,
    complete: (state) => state.complete,
    type: (state) => state.type,
    orderTypeId: (state) => state.orderTypeId,
    timeoutActive: (state) => state.timeoutActive,
    askingQuestions: (state) => state.askingQuestions,
    items: (state) => state.items,
    name: (state) => state.name,
    phone: (state) => state.phone,
    phoneCountryCode: (state) => state.phoneCountryCode,
    email: (state) => state.email,
    invoiceNumber: (state) => state.invoiceNumber,
    extraDetails: (state) => state.extraDetails,
    paymentType: (state) => state.paymentType,
    paymentMethods: (_, state, rootState) => {

      const orderType =
        rootState.location.config.orderTypes.find(
          (x) => x.id === state.orderTypeId
        ) || {};

      return orderType.paymentTypes || [];
    },
    paymentDetails: (state) => state.paymentDetails,
    hasPayment: (state) => state.paymentType !== null,
    subtotal: (state) => state.subtotal,
    tip: (state) => state.tip,
    taxTotal: (state) => state.taxTotal,
    grandTotal: (state, getters) => {
      if (state.posTotaled) {
        return state.posGrandTotal + state.tip;
      }
      const surchargesTotal = state.surcharges.reduce((acc, surcharge) => acc + surcharge.amount, 0);
      const { grandTotal } = calculateTotals(state.items, state.tip, state.taxTotal, getters.discounts, surchargesTotal);
      return grandTotal;
    },
    coupon: state => state.coupon,
    discounts: (state, getters, rootState) => {
      const coupon = state.coupon;
      if (!coupon) return 0;

      const excluded = Array.isArray(coupon.excluded_department_refs) ? coupon.excluded_department_refs : [];
      const excludedSet = new Set(excluded);

      // If nothing excluded, discount applies to full subtotal (existing behavior)
      let eligibleSubtotal = state.subtotal || 0;

      if (excludedSet.size > 0) {
        const itemToDeptRefs = new Map();
        const schedules = rootState?.menu?.schedules || [];
        schedules.forEach(schedule => {
          (schedule.departments || []).forEach(dept => {
            const deptRef = dept?.ref;
            (dept.items || []).forEach(it => {
              const itemRef = typeof it === 'string' ? it : it?.ref;
              if (!itemRef || !deptRef) return;
              if (!itemToDeptRefs.has(itemRef)) itemToDeptRefs.set(itemRef, new Set());
              itemToDeptRefs.get(itemRef).add(deptRef);
            });
          });
        });

        eligibleSubtotal = (state.items || []).reduce((acc, item) => {
          const deptRefs = itemToDeptRefs.get(item.itemRef);
          let isExcluded = false;
          if (deptRefs) {
            for (const ref of deptRefs) {
              if (excludedSet.has(ref)) {
                isExcluded = true;
                break;
              }
            }
          }
          return isExcluded ? acc : acc + (item.total || 0);
        }, 0);
      }

      if (coupon.type === '%') {
        return Math.round(eligibleSubtotal * (parseFloat(coupon.amount) / 100));
      } else if (coupon.type === '$') {
        return Math.min(Math.round(parseFloat(coupon.amount) * 100), eligibleSubtotal);
      }
      return 0;
    },
    total: (getters) => {
      if (getters.posTotaled) {
        return getters.posGrandTotal;
      }
      const subtotal = getters.subtotal;
      const discountAmount = getters.discounts;
      return Math.max(subtotal - discountAmount, 0);
    },
    barcodeLookup: (state) => state.barcodeLookup,
    barcodeActive: (state) => state.barcodeActive,
    surcharges: (state) => state.surcharges,
    gpattTicket: (state) => state.gpattTicket,
    confirmedOrderNumber: (state) => state.confirmedOrderNumber,
    submittedOrderNumber: (state) => state.submittedOrderNumber,
    receiptData: (state) => state.receiptData,
  },
  mutations: {
    RUM_SESSIONS(state, enabled) {
      state.datadogEnabled = enabled;
    },
    START(state, record = false) {
      state.datadogEnabled = record;
      if (!state.started && state.datadogEnabled) {
        datadogRum.startSessionReplayRecording();
        datadogRum.setUser({
          id: state.orderRef,
        });
        datadogRum.startView('Order Session', { orderRef: state.orderRef });
      }
      state.started = true;
      state.complete = false;
      state.startTime = new Date().toISOString();
    },
    RESET(state) {
      if (state.datadogEnabled) {
        datadogRum.clearUser();
      }
      this.dispatch("order/cancelOrder", { appRef: state.appRef });
      const defaultState = JSON.parse(newState);
      for (const key in state) {
        if (!Object.prototype.hasOwnProperty.call(defaultState, key)) {
          delete state[key];
        }
      }
      Object.assign(state, defaultState);

      state.paymentDetails = null;
      state.orderRef = uuidv4();
      setState(state);
      this.dispatch("customer/clearCustomer");

      this.dispatch("dialog/close");

      router.replace({ name: "start" });
    },
    SET_COUPON(state, coupon) {
      state.coupon = coupon;
      // Discounts are computed in the getter so we can respect excluded departments.
      state.discounts = 0;
      setState(state);
    },
    SET_COMPLETE(state, complete) {
      state.complete = complete;
    },
    SET_ORDER_CONFIRMED(state, confirmed) {
      state.isOrderConfirmed = confirmed;
      setState(state);
    },
    UPDATE_INTERVAL_TIMER(state, interval) {
      state.orderStatusInterval = interval;
    },
    SET_TIMEOUT_ACTIVE(state, payload) {
      state.timeoutActive = payload;
    },
    UPDATE_STATE(state, { key, value }) {
      state[key] = value;
      setState(state);
    },
    SET_ORDER_STATUS(state, status) {
      state.status = status;
      setState(state);
    },
    SET_ASKING_QUESTIONS: (state, value) => (state.askingQuestions = value),
    ADD_ITEM: (state, { itemRef, name, quantity, price, lineModifierSets, image, instructions, priceLocked = false }) => {
      if (!state.items) state.items = [];

      const item = {
        name,
        itemRef,
        image,
        instructions,
        quantity,
        price,
        lineModifierSets: lineModifierSets || [],
        priceLocked,
      };
      item.total = subtotalItem(item);
      state.items.unshift(item);

      const { subtotal } = calculateTotals(state.items, state.tip, state.taxTotal);
      state.subtotal = subtotal;
      setState(state);
    },
    UPDATE_ITEM(state, { index, itemRef, price, name, quantity, lineModifierSets, instructions }) {
      state.items[index] = {
        ...state.items[index],
        itemRef,
        price,
        name,
        quantity,
        lineModifierSets,
        instructions
      };
      state.items[index].total = subtotalItem(state.items[index]);
      setState(state);
    },
    UPDATE_ITEM_QUANTITY(state, { itemIndex, quantity }) {
      if (state.items[itemIndex]) {
        state.items[itemIndex].quantity = quantity;
        state.items[itemIndex].total = subtotalItem(state.items[itemIndex]);
        const { subtotal } = calculateTotals(state.items, state.tip, state.taxTotal);
        state.subtotal = subtotal;
      }
    },
    REMOVE_ITEM: (state, itemIndex) => {
      state.items.splice(itemIndex, 1);
      const { subtotal } = calculateTotals(state.items, state.tip, state.taxTotal);
      state.subtotal = subtotal;
      setState(state);
    },
    SET_TYPE: (state, type) => (state.type = type),
    SET_ORDER_TYPE_ID: (state, id) => (state.orderTypeId = id),
    SET_TICKET_NAME: (state, name) => (state.ticketName = name),
    SET_ITEMS: (state, items) => (state.items = items),
    SET_NAME: (state, name) => (state.name = name),
    SET_TIP: (state, tip) => {
      state.tip = tip;
      const { subtotal } = calculateTotals(state.items, tip, state.taxTotal);
      state.subtotal = subtotal;
    },
    SET_PHONE: (state, phone) => (state.phone = phone),
    SET_PHONE_COUNTRY_CODE: (state, phoneCountryCode) => (state.phoneCountryCode = phoneCountryCode),
    SET_OPT_IN: (state, optIn) => (state.optIn = optIn),
    SET_EXTRA_DETAILS: (state, extraDetails) => {
      const index = state.extraDetails.findIndex(detail => detail.key === extraDetails.key);
      if (index !== -1) {
        state.extraDetails[index].value = extraDetails.value;
      } else {
        state.extraDetails.push({
          key: extraDetails.key,
          value: extraDetails.value,
        });
      }
    },
    SET_PAYMENT_TYPE: (state, paymentType) => (state.paymentType = paymentType),
    SET_PAYMENT_DETAILS: (state, paymentDetails) =>
      (state.paymentDetails = paymentDetails),
    UPDATE_ORDER(state, order) {
      Object.keys(order).forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(state, key)) {
          state[key] = order[key];
        }
      });
      // Ensure gpattTicket is updated
      if (order.gpattTicket) {
        state.gpattTicket = order.gpattTicket;
      }
      // Ensure receiptData is updated
      if (order.receiptData) {
        state.receiptData = order.receiptData;
      }
      setState(state);
    },
    SET_TOTALS_LOADING: (state, value) => (state.totalsLoading = value),
    SET_CUSTOMER_DETAILS(state, { name, phone, phoneCountryCode = 'us', email }) {
      state.name = name;
      state.phone = phone;
      state.phoneCountryCode = phoneCountryCode;
      state.email = email;
    },
    SET_BARCODE_LOOKUP(state, barcodeLookup) {
      state.barcodeLookup = barcodeLookup;
    },
    SET_BARCODE_ACTIVE(state, isActive) {
      state.barcodeActive = isActive;
      setState(state);
    },
    SET_ACCEPTED_MESSAGES: (state, messages) => (state.acceptedMessages = messages),
    ADD_ACCEPTED_MESSAGE: (state, message) => {
      state.acceptedMessages.push({
        id: message.id,
        acceptedAt: new Date().toISOString()
      });
    },
  },
  actions: {
    setRumSessions({ commit }, enabled) {
      commit("RUM_SESSIONS", enabled);
    },
    async recalculateCartItemPrices({ state }) {
      const cartItems = state.items;
      cartItems.forEach(item => {
        if (item.priceLocked) {
          // Do not alter locked prices from weighted barcodes
          item.total = subtotalItem(item);
          return;
        }
        // get menu item from menu store
        const menuItem = this.getters["menu/items"].find(i => i.ref === item.itemRef);
        if (menuItem) {
          item.price = menuItem.price;

          // Update modifier prices
          if (item.lineModifierSets && menuItem.modifierSets) {
            item.lineModifierSets.forEach(lineModSet => {
              const menuModSet = menuItem.modifierSets.find(ms => ms.ref === lineModSet.modifierSetRef);
              if (menuModSet) {
                lineModSet.lineModifiers.forEach(lineMod => {
                  const menuMod = menuModSet.modifiers.find(m => m.ref === lineMod.modifierRef);
                  if (menuMod) {
                    lineMod.price = menuMod.price;

                    // Update nested modifier prices
                    if (lineMod.modifierSets && menuMod.modifierSets) {
                      lineMod.modifierSets.forEach(nestedLineModSet => {
                        const nestedMenuModSet = menuMod.modifierSets.find(nms => nms.ref === nestedLineModSet.modifierSetRef);
                        if (nestedMenuModSet) {
                          nestedLineModSet.modifiers.forEach(nestedLineMod => {
                            const nestedMenuMod = nestedMenuModSet.modifiers.find(nm => nm.ref === nestedLineMod.ref);
                            if (nestedMenuMod) {
                              nestedLineMod.price = nestedMenuMod.price;
                            }
                          });
                        }
                      });
                    }
                  }
                });
              }
            });
          }

          item.total = subtotalItem(item);
        }
      });

      state.items = cartItems;

      const { subtotal } = calculateTotals(state.items, state.tip, state.taxTotal);
      state.subtotal = subtotal;

    },
    async recalculateDiscounts({ commit, state }) {
      const coupon = state.coupon;
      if (coupon) {
        commit("SET_COUPON", coupon);
      }
    },
    removeCoupon({ commit }) {
      commit("SET_COUPON", null);
    },
    setBarcodeActive({ commit }, isActive) {
      commit("SET_BARCODE_ACTIVE", isActive);
    },
    async barcodeNotFound({ rootState }, barcode) {

      const response = await api.callAPI("POST", `v2/menu/barcodeNotFound`, { barcode }, rootState.terminal.apiToken);
      return response;
    },
    async start({ commit, rootState, state, getters }, orderTypeId = null) {
      orderActive(rootState.locationId, state.orderRef);
      commit("START", rootState?.location?.config?.rumSessions || false);

      try {
        const payload = {};
        if (state.phone) payload.phone = state.phone;
        // Use provided orderTypeId parameter, or fall back to state.orderTypeId
        const idToUse = orderTypeId || state.orderTypeId;
        if (idToUse) payload.orderTypeId = idToUse;

        const response = await api.callAPI(
          "POST",
          `v2/order/start`,
          payload,
          rootState.terminal.apiToken
        );

        if (response && response.appRef) {
          commit("UPDATE_STATE", { key: "appRef", value: response.appRef });
          await getters.logger.info(`Pending order started`, { appRef: response.appRef });
        } else {
          await getters.logger.error("No appRef returned from start endpoint");
        }
      } catch (error) {
        await getters.logger.error("Failed to start pending order", { error });
        // Fail-open: continue UX even if start fails
      }
    },
    resetOrder({ commit, dispatch, rootState, state, getters }, source = "unknown") {
      orderInactive(rootState.locationId, state.orderRef);
      if (source === 'idle') {
        getters.logger.info(`Resetting order state due to inactivity`);
      }
      getters.logger.info(`Resetting order state from ${source}`);
      commit("RESET");

      // On idle timeout OR start-over/reset flows, force language back to configured default
      // (or en if language support is disabled) so the next user starts in the default language.
      if (source === 'idle' || source === 'startOver' || source === 'start_over' || source === 'unknown' || !source) {
        const languageSupport = rootState?.location?.config?.languageSupport || {};
        const defaultLanguage = languageSupport.defaultLanguage || 'en';
        const nextLanguage = languageSupport.enabled ? defaultLanguage : 'en';
        dispatch('setLanguage', nextLanguage, { root: true });
      }
    },
    async setBarcodeLookup({ commit }, barcodeLookup) {
      commit("SET_BARCODE_LOOKUP", barcodeLookup);
    },
    async cancelOrder({ rootState, getters }, { appRef }) {
      if (appRef) {
        try {
          await api.callAPI(
            "POST",
            `v2/order/cancel/${appRef}`,
            {},
            rootState.terminal.apiToken
          );
          await getters.logger.info(`Order canceled successfully`, { appRef: appRef });
        } catch (error) {
          await getters.logger.error("Error canceling order", { error, appRef: appRef });
        }
      } else {
        await getters.logger.error("No appRef found, cannot cancel order");
      }
    },
    async clearPendingTicket({ state, rootState }) {

      if (!state.appRef) {
        return;
      }

      try {
        const response = await api.callAPI(
          "POST",
          `order/clear-pending-ticket/${state.appRef}`,
          null,
          rootState.terminal.apiToken
        );

        if (response.message === "Ticket voided successfully") {
          logger.info("Ticket voided successfully");
        } else if (response.message === "No ticket to void") {
          logger.info("No ticket to void");
        }
      } catch (error) {
        logger.error("Error clearing pending ticket", error);
        throw error;
      }
    },
    async sendReceipt({ state, rootState }, { type, to }) {
      try {
        const request = {
          type,
          to
        };

        api.callAPI(
          "POST",
          `v2/order/notify/${state.appRef}`,
          request,
          rootState.terminal.apiToken
        );

        return true;
      } catch (error) {
        logger.error("Error sending receipt", error);
        return false;
      }
    },
    setPaymentType({ commit }, paymentType) {

      commit("SET_PAYMENT_TYPE", paymentType);
      if (paymentType === "CASH") {
        commit("SET_PAYMENT_DETAILS", null);
        commit("SET_TIP", 0);
      }
    },
    setPhone({ commit }, { phone }) {
      commit("SET_PHONE", phone);
    },
    async signIn({ dispatch }, { phone }) {
      try {
        await dispatch("customer/fetchCustomer", { phoneNumber: phone }, { root: true });
        return Promise.resolve();
      } catch (error) {
        console.error("Sign-in error:", error);
        return Promise.reject(error);
      }
    },
    signOut({ dispatch }) {
      dispatch("customer/clearCustomer", null, { root: true });
    },
    setTimeoutActive({ commit }, payload) {
      commit("SET_TIMEOUT_ACTIVE", payload);
    },
    setPaymentDetails({ commit }, paymentDetails) {
      commit("SET_PAYMENT_DETAILS", paymentDetails);
    },
    async setOrderType({ commit, rootGetters, getters, state, dispatch }, ot) {
      const orderType =
        rootGetters["location/config"].orderTypes.find(
          (x) => x.id === ot.id
        ) || {};

      if (!state.started) {
        await getters.logger.info(`Starting order with type ${orderType.name}`);
      } else {
        await getters.logger.info(`Changing order type to ${orderType.name}`);
      }


      commit("SET_TYPE", orderType.type);
      commit("SET_ORDER_TYPE_ID", orderType.id);
      commit("SET_PAYMENT_TYPE", null);


      const availablePaymentMethods = orderType.paymentTypes || [];
      if (availablePaymentMethods.length === 1) {
        commit("SET_PAYMENT_TYPE", availablePaymentMethods[0]);
      }
      commit("UPDATE_STATE", { key: "type", value: orderType.type });
      // Dispatch action from another store
      await dispatch('menu/setPriceRule', { type: orderType.priceAdjustType, amount: orderType.priceAdjustAmount }, { root: true });
      await dispatch('order/recalculateCartItemPrices', null, { root: true });
      await dispatch('order/recalculateDiscounts', null, { root: true });

    },
    async fillOrderDetails({ commit, state }, answers) {
      answers.forEach((answer) => {

        if (Object.keys(state).includes(answer.orderProperty)) {
          commit("UPDATE_STATE", { key: answer.orderProperty, value: answer.answer });
        } else if (answer.posLabel) {
          commit("SET_EXTRA_DETAILS", {
            key: answer.posLabel,
            value: answer.answer,
          });
        }
        if (answer.useAsTicketName) {
          commit("SET_TICKET_NAME", answer.answer);
        }
      });
    },
    async updateTotals({ rootState, state, commit, dispatch }) {
      commit("SET_TOTALS_LOADING", true);

      try {
        const locationId = rootState.locationId;

        const request = {
          locationId,
          context: "kiosk",
          order: state,
        };

        const response = await api.callAPI(
          "POST",
          `order/calculate`,
          request,
          rootState.terminal.apiToken
        );
        // Update all relevant totals
        commit("UPDATE_ORDER", {
          ...response,
          subtotal: response.subtotal || state.subtotal,
          tip: response.tip || state.tip,
          taxTotal: response.taxTotal || state.taxTotal,
        });

        // Recalculate totals based on updated state
        if (!response.posTotaled) {
          const { subtotal } = calculateTotals(state.items, state.tip, state.taxTotal);
          commit("UPDATE_STATE", { key: "subtotal", value: subtotal });
        } else {
          commit("UPDATE_STATE", { key: "subtotal", value: response.subtotal });
        }
      } catch (error) {
        dispatch("error", error);
        throw error;  // Throw the error to be caught by the calling function
      } finally {
        commit("SET_TOTALS_LOADING", false);
      }
    },
    async validateOrder({ state, rootState, commit }) {
      try {
        const locationId = rootState.locationId;
        const request = {
          locationId,
          context: "kiosk",
          order: state,
        };

        const response = await api.callAPI(
          "POST",
          `order/calculate`,
          request,
          rootState.terminal.apiToken
        );
        if (response.orderRef) {
          commit("UPDATE_ORDER", {
            ...response,
            subtotal: response.subtotal || state.subtotal,
            tip: response.tip || state.tip,
            taxTotal: response.taxTotal || state.taxTotal,
          });

          // Recalculate totals based on updated state
          if (!response.posTotaled) {
            const { subtotal } = calculateTotals(state.items, state.tip, state.taxTotal);
            commit("UPDATE_STATE", { key: "subtotal", value: subtotal });
          } else {
            commit("UPDATE_STATE", { key: "subtotal", value: response.subtotal });
          }
          return { success: true };
        } else {
          console.error(response);
          return { success: false, errors: response };
        }
      } catch (error) {
        return { success: false, errors: [error?.response?.data?.error || error?.message || error || 'Unknown error'] };
      }
    },
    async validate({ dispatch, getters, commit }) {
      dispatch("setTimeoutActive", false);
      dispatch("loading", 'validatingOrder', { root: true });
      commit("UPDATE_STATE", { key: "discounts", value: getters.discounts });

      let submitted = false;

      const response = {
        success: true,
        errorMessage: null
      };
      try {
        const validationResponse = await dispatch("validateOrder");
        if (!validationResponse.success) {
          response.success = false;
          response.errorMessage = validationResponse.errors.join(", ");
          return response;
        }

        if (getters.grandTotal === 0) {
          submitted = true;
          await dispatch("submit");
        }

        return response;
      } catch (error) {
        response.success = false;
        response.errorMessage = error?.response?.data?.error || error?.message || error || 'Unknown error';
        return response;
      } finally {
        if (!submitted) {
          dispatch("doneLoading", null, { root: true });
        }
      }
    },
    subscribeToOrderUpdates({ rootState, state, dispatch, commit }, orderId) {

      let pollingTimeout;
      let loadingMessageTimeout;
      let pollCount = 0;
      let giveUpTimeout;

      const clearTimers = () => {
        clearTimeout(pollingTimeout);
        clearTimeout(loadingMessageTimeout);
        clearTimeout(giveUpTimeout);
      };

      const handleGiveUp = () => {
        dispatch("setTimeoutActive", true);
        clearTimers();
        if (this.websocket) {
          this.websocket.unsubscribeFromOrder(orderId);
        }
        appError("Order verification timed out", "We could not verify the status of your order. Please contact support.", false, true);
        dispatch("doneLoading", null, { root: true });
      };

      const checkStatus = async () => {

        if (state.isOrderConfirmed || !state.started) return; // Order has already been confirmed

        try {
          // Prefer the newer details endpoint (includes callNumber/receiptData). Fall back to legacy status endpoint.
          let response;
          try {
            response = await api.callAPI("GET", `v2/order/status-details/${orderId}`, null, rootState.terminal.apiToken);
          } catch (e) {
            response = await api.callAPI("GET", `v2/order/status/${orderId}`, null, rootState.terminal.apiToken);
          }

          dispatch("updateOrderStatus", {
            status: response?.status || response,
            receiptData: response?.receiptData,
            callNumber: response?.callNumber || null
          });
          pollCount++;

          if (!["CONFIRMED", "REJECTED", "FULFILLED", "CANCELED", "FAILED"].includes(response?.status || response)) {
            pollingTimeout = setTimeout(checkStatus, 5000);
          } else {
            clearTimers();
            dispatch("setTimeoutActive", true);
            if (this.websocket) {
              this.websocket.unsubscribeFromOrder(orderId);
            }
            commit("SET_ORDER_CONFIRMED", true);

            dispatch("doneLoading", null, { root: true });
          }
        } catch (error) {
          dispatch("setTimeoutActive", true);
          clearTimers();
          if (this.websocket) {
            this.websocket.unsubscribeFromOrder(orderId);
          }
          appError("Order was rejected", error || "An error occurred while processing your order.", false, true);
          dispatch("doneLoading", null, { root: true });
        }
      };

      const updateLoadingMessage = () => {
        if (["CONFIRMED", "REJECTED", "FULFILLED", "CANCELED", "FAILED"].includes(state.status)) {
          clearTimers();
          dispatch("doneLoading", null, { root: true });
          return;
        }

        if (pollCount === 3) {
          dispatch("loading", 'oneMoment', { root: true });
        } else if (pollCount === 6) {
          dispatch("loading", 'oneMoreMoment', { root: true });
        }
        loadingMessageTimeout = setTimeout(updateLoadingMessage, 5000);
      };

      pollingTimeout = setTimeout(checkStatus, 5000);
      loadingMessageTimeout = setTimeout(updateLoadingMessage, 5000);
      giveUpTimeout = setTimeout(handleGiveUp, 60000);

      if (rootState.isWebSocketConnected) {

       /*
        this.websocket.subscribeToOrder(orderId, (status) => {
          dispatch("updateOrderStatus", status);
          if (["CONFIRMED", "REJECTED", "FULFILLED", "CANCELED", "FAILED"].includes(status)) {
            commit("SET_ORDER_CONFIRMED", true);
            this.unsubscribeFromOrder(orderId);
            clearTimers();
          }
        });
        */
      } else {
        logger.error("WebSocket is not connected. Cannot subscribe to order updates.");
      }
    },
    updateOrderStatus({ commit, state, dispatch, rootState }, { status, reasons, receiptData, callNumber }) {

      if (state.isOrderConfirmed || !state.started) return; // Order has already been confirmed

      commit("SET_ORDER_STATUS", status);

      // OLO: if Conecto provides a callNumber on confirmation, treat it as the source of truth
      const locationConfig = rootState?.location?.config || {};
      const usePosOrderNumber =
        locationConfig?.rails === "olo" &&
        (locationConfig?.oloUsePosOrderNumber === true || locationConfig?.oloUseCallNumberForOrderNumber === true); // backward compatible
      if (usePosOrderNumber && status === "CONFIRMED" && callNumber) {
        commit("UPDATE_STATE", { key: "confirmedOrderNumber", value: callNumber });
      }
      
      // Store receiptData if provided
      if (receiptData) {
        commit("UPDATE_ORDER", { receiptData });
      }
      
      if (["CONFIRMED", "REJECTED", "FULFILLED", "CANCELED", "FAILED"].includes(status)) {
        clearInterval(state.orderStatusInterval);
        dispatch("doneLoading", null, { root: true });
      }
      if (status === "REJECTED") {
        commit("SET_COMPLETE", true);
        const errors = reasons.map((r) => r.explanation || r.code) || [];
        const message = `Order was rejected: ${errors.join(", ")}`;
        appError("Error", message, false, true);
      } else if (status === "CONFIRMED" || status === "FULFILLED") {
        commit("SET_COMPLETE", true);
        if (router.currentRoute.name !== "menu" && router.currentRoute.name !== "start" && state.appRef) {
          router.replace({ name: "complete" });
        }
      }
    },
    async submit({ rootState, state, dispatch, commit, getters }) {
      dispatch("setTimeoutActive", false);
      dispatch("loading", 'submittingOrder', { root: true });

      if (getters.total === 0) {
        commit("SET_PAYMENT_TYPE", "CASH");
      }

      try {
        const locationId = rootState.locationId;
        const request = {
          locationId,
          paymentType: state.paymentType,
          paymentDetails: state.paymentDetails,
          appRef: state.appRef,
        };

        const response = await api.callAPI(
          "POST",
          `order/commit`,
          request,
          rootState.terminal.apiToken
        );
        if (response.success) {
          // OLO: capture the provisional order number generated at submission time.
          // This allows pickup chits to print even when we are NOT preferring the POS-provided number.
          if (response?.order?.orderNumber) {
            commit("UPDATE_STATE", { key: "submittedOrderNumber", value: response.order.orderNumber });
          }

          // Store printed receipt if available
          if (response.receiptData) {
            commit("UPDATE_ORDER", { receiptData: response.receiptData });
          }
          
          if (response.webhookRef) {
            // handle conecto order
            this.dispatch("order/subscribeToOrderUpdates", response.webhookRef);
            this.dispatch("loading", 'submittingOrder', { root: true });
          } else {
            router.replace({ name: "complete" });
            commit("SET_ORDER_CONFIRMED", true);
            dispatch("doneLoading", null, { root: true });
          }
          return { success: true };
        } else {
          //commit("SET_COMPLETE", true);
          // dispatch("error", { message: response.details });
          dispatch("setTimeoutActive", true);
          dispatch("doneLoading", null, { root: true });
          commit("SET_PAYMENT_DETAILS", null);
          commit("SET_TIP", null);
          return { success: false, errorMessage: response.details };
        }
      } catch (error) {
        dispatch("setTimeoutActive", true);
        commit("SET_COMPLETE", true);
        dispatch("doneLoading", null, { root: true });
        commit("SET_PAYMENT_DETAILS", null);
        commit("SET_TIP", null);
        return false;
      }
    },
    async addItem({ commit, dispatch, state, rootGetters }, item) {
      // Check if there's a maxItems restriction for the current order type
      const orderType = rootGetters["location/config"].orderTypes.find(
        (x) => x.id === state.orderTypeId
      );

      if (orderType && orderType.maxItems && orderType.maxItems > 0) {
        const currentItemCount = state.items.length;
        const newItemCount = currentItemCount + (item.quantity || 1);

        if (newItemCount > orderType.maxItems) {
          // Show error dialog and prevent adding item
          this.dispatch("dialog/openDialog", {
            title: "Order Limit Reached",
            message: `You can only add up to ${orderType.maxItems} items to your order. Please remove some items before adding more.`,
            showCancel: false,
            okText: "OK"
          });
          return false;
        }
      }
      
      commit("ADD_ITEM", item);
      await dispatch("recalculateDiscounts");
      return true;
    },
    async removeItem({ commit, dispatch }, itemIndex) {
      commit("REMOVE_ITEM", itemIndex);
      await dispatch("recalculateDiscounts");
    },
    async updateItemQuantity({ commit, dispatch }, payload) {
      commit('UPDATE_ITEM_QUANTITY', payload);
      await dispatch("recalculateDiscounts");
    },
    async updateItem({ commit, dispatch }, { index, itemRef, price, name, quantity, lineModifierSets, instructions }) {
      commit('UPDATE_ITEM', { index, itemRef, price, name, quantity, lineModifierSets, instructions });
      await dispatch("recalculateDiscounts");
    },
    error({ commit, dispatch }, error) {
      console.error("Error:", error);
      const dialog = {
        title: "Error",
        message: error.message || "An error occurred",
        type: "error",
      }
      commit("SET_ERROR", dialog, { root: true });
      dispatch("doneLoading", null, { root: true });
      router.push({ name: "error" });
    },
    updateCustomerDetails({ commit }, customerDetails) {
      commit('SET_CUSTOMER_DETAILS', customerDetails);
    },
    showErrorScreen({ commit }, { title, message }) {
      commit("SET_ERROR", { title, message, startOver: true, back: true }, { root: true });
      router.push({ name: "error" });
    },
    acceptMessage({ commit }, message) {
      commit('ADD_ACCEPTED_MESSAGE', message);
    },
  },
};

